import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import styled from 'styled-components';

import { translator } from 'mage-i18n';
import { Link, Typography, SpacerHorizontal, SpacerVertical } from '@klarna/bubble-ui';
import { analytics, defaultContainerPropTypes, HorizontalWrapper } from 'moet-core';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';
import { withResponsive, VerticalRhythm } from 'mage-components';

import * as a from '@/actions/signup';
import {
  CallToActionButton,
  ContainerTemplate,
  CardComponent,
  BackButton,
  withLeadUpdateOnNext,
} from '@/components';
import { getMerchantSensitiveGood } from '@/selectors/data';

import { ANALYTICS_EVENTS } from './analyticsEvents';
import {
  CY_SENSITIVE_GOODS_CONTAINER_IDS,
  DEFAULT_SENSITIVE_GOODS_VALUE,
  linkEthicalInstrustions,
  SENSITIVE_GOODS_TO_MARKETO,
  SENSITIVE_GOODS,
  T_PREFIX,
} from './constants';

const SensitiveGoodsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
`;

export const _SensitiveGoods = ({
  isDisabled,
  isLoading,
  onBack,
  onChangeSensitiveGoodsFlag,
  onNext,
  t,
  isMobile,
  sellsSensitiveGoods: sensitiveGoods,
}) => {
  const [sellsSensitiveGoods, setSelectedGood] = useState(
    sensitiveGoods || DEFAULT_SENSITIVE_GOODS_VALUE,
  );

  const onSelect = category => {
    setSelectedGood(category);
  };

  const isSelected = id => {
    return id === sellsSensitiveGoods;
  };

  const onContinue = async () => {
    const sensitiveGoodsValue =
      SENSITIVE_GOODS_TO_MARKETO[sellsSensitiveGoods] || SENSITIVE_GOODS_TO_MARKETO.NONE;
    // we send goods category id to redux
    await onChangeSensitiveGoodsFlag(sellsSensitiveGoods);
    analytics.logEvent(ANALYTICS_EVENTS.submitSensitiveGoods, {
      sellsSensitiveGoods: sensitiveGoodsValue,
    });

    // we send goods category value to marketo
    onNext({ leadData: { sellsSensitiveGoods: sensitiveGoodsValue } });
  };

  return (
    <ContainerTemplate title={t(`${T_PREFIX}.title_2`)} showRecaptchaFooter>
      <Typography textToken="text-style/text/super-paragraphs/body/regular">
        {t(`${T_PREFIX}.subtitle_2`)}
      </Typography>
      <VerticalRhythm spacing={{ mobile: 6, desktop: 10 }} />
      <SensitiveGoodsWrapper>
        {SENSITIVE_GOODS.map(category => (
          <CardComponent
            key={category}
            id={category}
            isMobile={isMobile}
            isSelectedFn={isSelected}
            onChange={onSelect}
            cyElementId={generateCyElemId(
              CY_SENSITIVE_GOODS_CONTAINER_IDS.OPTION(category),
              window,
            )}
          />
        ))}
      </SensitiveGoodsWrapper>
      <Link
        isUnderlined
        href={linkEthicalInstrustions}
        target="_blank"
        textToken="text-style/text/labels/body/medium"
        onPress={() => analytics.logEvent(ANALYTICS_EVENTS.linkClicked)}
      >
        {t(`${T_PREFIX}.link`)}
      </Link>
      <SpacerVertical large />
      <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
        <BackButton
          tabIndex={2}
          onClick={onBack}
          disabled={isDisabled}
          {...generateCyElemId(CY_SENSITIVE_GOODS_CONTAINER_IDS.BTN_BACK, window)}
        />
        <SpacerHorizontal medium />
        <CallToActionButton
          tabIndex={9}
          onClick={onContinue}
          loading={isLoading}
          disabled={isDisabled}
          {...generateCyElemId(CY_SENSITIVE_GOODS_CONTAINER_IDS.BTN_CONTINUE, window)}
        >
          {t(`${T_PREFIX}.buttons.continue`)}
        </CallToActionButton>
      </HorizontalWrapper>
      <SpacerVertical small />
    </ContainerTemplate>
  );
};

_SensitiveGoods.propTypes = {
  ...defaultContainerPropTypes,
  isMobile: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  onChangeSensitiveGoodsFlag: PropTypes.func.isRequired,
  sellsSensitiveGoods: PropTypes.string,
};

export const SensitiveGoodsCards = compose(
  connect(
    state => ({ t: translator(state), sellsSensitiveGoods: getMerchantSensitiveGood(state) }),
    dispatch => ({
      onChangeSensitiveGoodsFlag: sellsSensitiveGoods =>
        new Promise(resolve => {
          a.onChangeFormValue('sellsSensitiveGoods', sellsSensitiveGoods)(dispatch);
          resolve();
        }),
    }),
  ),
  withLeadUpdateOnNext,
  withResponsive,
)(_SensitiveGoods);
